import React, { useState } from "react";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import ContactsIcon from "@mui/icons-material/Contacts";
import { ProfileData } from "../types/profile";
import LoadingScreen from "../../../components/LoadingScreen";
import ProfileAvatar from "./ProfileAvatar";
import ProfileDetailRow from "./ProfileDetailRow";
import CommonButtons from "../../../components/CommonButtons";
import { useImageUpload } from "../hooks/useImageUpload";
import useUsers from "src/business-types/grocery/hooks/useUsers";
import { useUser } from "src/shared/context/UserContext";

interface UserProfileProps {
  profileData: ProfileData;
  onEdit: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ profileData, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const { uploadImage } = useImageUpload();
  const { user } = useUser();
  const { reload } = useUsers()

  const handleImageUpload = async (file: File) => {
    setLoading(true);

    const reader = new FileReader();
    const img = document.createElement("img");

    reader.onload = () => {
      if (typeof reader.result === "string") {
        img.src = reader.result;
      }
    };

    img.onload = async () => {
      const canvas = document.createElement("canvas");

      const maxWidth = 200;
      const scaleFactor = maxWidth / img.width;

      canvas.width = maxWidth;
      canvas.height = img.height * scaleFactor;

      const ctx = canvas.getContext("2d");

      if (!ctx) {
        setLoading(false);
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async (blob) => {
          if (blob && blob.size) {
            try {
              const compressedFile = new File(
                [blob],
                "image.png",
                {
                  type: "image/png",
                }
              );

              await uploadImage(
                compressedFile,
                `/pos/v2/image/${profileData._id.$oid}`,
                "put",
                `${user?.userName}.png`,
                `Profilelogo${user?.userName}`
              );

              // Refresh user data without reload
              reload()
            } catch (error) {
              console.error(error);
            } finally {
              setLoading(false);
            }
          } else {
            setLoading(false);
          }
        },
        "image/png"
      );
    };

    reader.readAsDataURL(file);
  };

  const formatDateDDMMYYYY = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row items-start gap-6">
        {/* Avatar Upload */}
        <ProfileAvatar image={String(user?.profileImageUrl)} onImageChange={handleImageUpload} tooltipText="Change Photo" />
        {/* Profile Details */}
        <div className="flex-1 space-y-4">
          <ProfileDetailRow icon={<PersonIcon />} label="Name" value={`${profileData.firstName} ${profileData.middleName} ${profileData.lastName}`} />
          <ProfileDetailRow icon={<CalendarMonthIcon />} label="Date of birth" value={formatDateDDMMYYYY(typeof profileData.dob === "string" ? profileData.dob : profileData.dob?.$date)} />{" "}
          <ProfileDetailRow icon={<ContactsIcon />} label="Phone Number" value={profileData.phones?.[0]?.phone || ""} />{" "}
          <ProfileDetailRow icon={<EmailIcon />} label="Email ID" value={profileData.emails?.[0]?.email || ""} />{" "}
          <ProfileDetailRow icon={<HomeIcon />} label="Address" value={profileData.addressLine1} />
          <CommonButtons label="Edit Profile" onClick={onEdit} />
        </div>
      </div>
      {loading && <LoadingScreen />}
    </div>
  );
};

export default UserProfile;
